import { Effect } from '../core/modules/EffectsLayer/Effect';
import { EffectsLayer } from '../core/modules/EffectsLayer/EffectsLayer';
import { EffectStackState } from '../core/modules/EffectsLayer/EffectStack';
import { LevelManager } from './LevelManager';

export class SplatEffectsLayer extends EffectsLayer {
  public static splatBank: string[] = ['splat-01.png', 'splat-02.png', 'splat-03.png', 'splat-04.png', 'splat-05.png', 'splat-06.png', 'splat-07.png'];
  public static splatBanana: string[] = ['banana-lump.png', 'banana-skin-1.png', 'banana-skin-2.png'];
  public static splatLeaf: string[] = ['leaf-1.png', 'leaf-2.png', 'leaf-3.png'];

  public static hexBananaDay: number = 0xffffff;
  public static hexBananaNight: number = 0x545d61;
  public static hexConcreteDay: number = 0x545d61;
  public static hexConcreteNight: number = 0x353b3d;
  public static hexOilDay: number = 0x161616;
  public static hexOilNight: number = 0x0f0f0f;
  public static hexIceDay: number = 0xc3e2ef;
  public static hexIceNight: number = 0x91a9b3;
  public static hexMudDay: number = 0xb46e01;
  public static hexMudNight: number = 0x825001;
  public static hexSnowDay: number = 0xd7edef;
  public static hexSnowNight: number = 0x98a8a9;
  public static events = {
    SplatBanana: 'splatBanana',
    SplatConcrete: 'splatConcrete',
    SplatOil: 'splatOil',
    SplatIce: 'splatIce',
    SplatLeaf: 'splatLeaf',
    SplatMud: 'splatMud',
    SplatSnow: 'splatSnow',
    SplatSmoke: 'splatSmoke'
  } as const;

  protected levelManager: LevelManager;

  constructor(scene: Phaser.Scene, effectsLayer: Phaser.GameObjects.Layer, levelManager: LevelManager) {
    super(scene, effectsLayer);

    this.levelManager = levelManager;

    this.scene.events.on(SplatEffectsLayer.events.SplatBanana, this.showBananaSplats, this);
    this.scene.events.on(SplatEffectsLayer.events.SplatConcrete, this.showConcreteSplats, this);
    this.scene.events.on(SplatEffectsLayer.events.SplatOil, this.showOilSplats, this);
    this.scene.events.on(SplatEffectsLayer.events.SplatIce, this.showIceSplats, this);
    this.scene.events.on(SplatEffectsLayer.events.SplatLeaf, this.showLeafSplats, this);
    this.scene.events.on(SplatEffectsLayer.events.SplatMud, this.showMudSplats, this);
    this.scene.events.on(SplatEffectsLayer.events.SplatSnow, this.showSnowSplats, this);
    this.scene.events.on(SplatEffectsLayer.events.SplatSmoke, this.showSmokeSplats, this);
  }

  public showConcreteSplats(startX: number, startY: number, splatCount: number) {
    let targets: Effect[] = [];

    let splats: string[] = this.randomSplats(splatCount, SplatEffectsLayer.splatBank);
    let endPositions = this.getRandomBoxDistribution(0, 0, 1920, 768, 20, 8, splatCount);
    // console.log('endPositions', endPositions);

    for (let i = 0; i < splatCount; i++) {
      let effect = this.getFreeEffect(startX, startY, 'game', splats[i]);
      effect.tint = this.levelManager.CurrentTheme.isNight ? SplatEffectsLayer.hexConcreteNight : SplatEffectsLayer.hexConcreteDay;
      targets.push(effect);
    }

    this.showEffectStack({
      state: EffectStackState.Ready,
      targets: targets,
      endPoints: endPositions,
      effects: [
        { function: this.throwEffect.bind(this) },
        { function: this.slideEffect.bind(this), params: { duration: 1500 } },
        { function: this.fadeOutEffect.bind(this) }
      ]
    });
  }

  public showOilSplats(startX: number, startY: number, splatCount: number) {
    let targets: Effect[] = [];

    targets = this.createScreenEdgeEffects(targets,'oil-horizontal.png','oil-vertical.png');

    let endPositions = this.getRandomBoxDistribution(0, 0, 1920, 768, 20, 8, splatCount);

    this.showEffectStack({
      state: EffectStackState.Ready,
      targets: targets,
      endPoints: endPositions,
      effects: [
        { function: this.fadeInEffect.bind(this) },
        { function: this.fadeOutEffect.bind(this),params:{ delay:800} }
      ],
      effectParams: {
        targetKey: 'game',
        targetFrame: 'splat-02.png'
      }
    });
  }

  public showMudSplats(startX: number, startY: number, splatCount: number) {
    let targets: Effect[] = [];

    let splats: string[] = this.randomSplats(splatCount, SplatEffectsLayer.splatBank);
    let endPositions = this.getRandomBoxDistribution(0, 0, 1920, 768, 20, 8, splatCount);
    console.log('endPositions', endPositions);

    for (let i = 0; i < splatCount; i++) {
      let effect = this.getFreeEffect(startX, startY, 'game', splats[i]);
      effect.tint = this.levelManager.CurrentTheme.isNight ? SplatEffectsLayer.hexMudNight : SplatEffectsLayer.hexMudDay;
      targets.push(effect);
    }

    this.showEffectStack({
      state: EffectStackState.Ready,
      targets: targets,
      endPoints: endPositions,
      effects: [
        { function: this.throwEffect.bind(this) },
        { function: this.slideEffect.bind(this), params: { duration: 1500 } },
        { function: this.fadeOutEffect.bind(this) }
      ]
    });
  }

  public showSnowSplats(startX: number, startY: number, splatCount: number) {
    let targets: Effect[] = [];

    targets = this.createScreenEdgeEffects(targets,'freeze-horizontal.png','freeze-vertical.png');

    let endPositions = this.getRandomBoxDistribution(0, 0, 1920, 768, 20, 8, splatCount);

    this.showEffectStack({
      state: EffectStackState.Ready,
      targets: targets,
      endPoints: endPositions,
      effects: [
        { function: this.fadeInEffect.bind(this) },
        { function: this.fadeOutEffect.bind(this),params:{ delay:800} }
      ],
      effectParams: {
        targetKey: 'game',
        targetFrame: 'splat-02.png'
      }
    });
  }

  public showSmokeSplats(startX: number, startY: number, splatCount: number) {
    let targets: Effect[] = [];

    targets = this.createScreenEdgeEffects(targets,'smoke-horizontal.png','smoke-vertical.png');

    let endPositions = this.getRandomBoxDistribution(0, 0, 1920, 768, 20, 8, splatCount);

    this.showEffectStack({
      state: EffectStackState.Ready,
      targets: targets,
      endPoints: endPositions,
      effects: [
        { function: this.fadeInEffect.bind(this) },
        { function: this.fadeOutEffect.bind(this),params:{ delay:800} }
      ],
      effectParams: {
        targetKey: 'game',
        targetFrame: 'splat-02.png'
      }
    });
  }

  public createScreenEdgeEffects(targets:Effect[], horizontalImage:string, verticalImage:string)
  {
    let bottomEffect = this.getFreeEffect(this.scene.game.canvas.width/2, this.scene.game.canvas.height,'game',horizontalImage);
    bottomEffect.y -= bottomEffect.height/2;
    targets.push(bottomEffect);
    
    let topEffect = this.getFreeEffect(this.scene.game.canvas.width/2, 0,'game',horizontalImage);
    topEffect.y += bottomEffect.height/2;
    topEffect.angle = 180;
    targets.push(topEffect);

    let heightMod = 768 / window.innerHeight;
    let left = 1920 / 2 - (window.innerWidth * heightMod) / 2;
    left = Math.max(left, 0);
    let leftEffect = this.getFreeEffect(left, this.scene.game.canvas.height/2,'game',verticalImage);
    leftEffect.x += leftEffect.width/2;
    targets.push(leftEffect);

    let right = 1920 / 2 + (window.innerWidth * heightMod) / 2;
    right = Math.min(right, 1920);
    let rightEffect = this.getFreeEffect(right, this.scene.game.canvas.height/2,'game',verticalImage);
    rightEffect.x -= rightEffect.width/2;
    rightEffect.angle = 180;
    targets.push(rightEffect);

    return targets;
  }

  public showBananaSplats(startX: number, startY: number, splatCount: number) {
    let targets: Effect[] = [];

    let splats: string[] = this.randomSplats(splatCount, SplatEffectsLayer.splatBanana);
    let endPositions = this.getRandomBoxDistribution(0, 0, 1920, 768, 20, 8, splatCount);
    console.log('endPositions', endPositions);

    for (let i = 0; i < splatCount; i++) {
      let effect = this.getFreeEffect(startX, startY, 'game', splats[i]);
      if (this.levelManager.CurrentTheme.isNight) effect.tint = SplatEffectsLayer.hexBananaNight;
      targets.push(effect);
    }

    this.showEffectStack({
      state: EffectStackState.Ready,
      targets: targets,
      endPoints: endPositions,
      effects: [
        { function: this.throwEffect.bind(this) },
        { function: this.slideEffect.bind(this), params: { duration: 1500 } },
        { function: this.fadeOutEffect.bind(this) }
      ]
    });
  }

  public showIceSplats(startX: number, startY: number, splatCount: number) {
    let targets: Effect[] = [];

    let splats: string[] = this.randomSplats(splatCount, SplatEffectsLayer.splatBank);
    let endPositions = this.getRandomBoxDistribution(0, 0, 1920, 768, 20, 8, splatCount);
    console.log('endPositions', endPositions);

    for (let i = 0; i < splatCount; i++) {
      let effect = this.getFreeEffect(startX, startY, 'game', splats[i]);
      effect.tint = this.levelManager.CurrentTheme.isNight ? SplatEffectsLayer.hexIceNight : SplatEffectsLayer.hexIceDay;
      targets.push(effect);
    }

    this.showEffectStack({
      state: EffectStackState.Ready,
      targets: targets,
      endPoints: endPositions,
      effects: [
        { function: this.throwEffect.bind(this) },
        { function: this.slideEffect.bind(this), params: { duration: 1500 } },
        { function: this.fadeOutEffect.bind(this) }
      ]
    });
  }

  public showLeafSplats(startX: number, startY: number, splatCount: number) {
    let targets: Effect[] = [];

    let splats: string[] = this.randomSplats(splatCount, SplatEffectsLayer.splatLeaf);
    let endPositions = this.getRandomBoxDistribution(0, 0, 1920, 768, 20, 8, splatCount);
    console.log('endPositions', endPositions);

    for (let i = 0; i < splatCount; i++) {
      let effect = this.getFreeEffect(startX, startY, 'game', splats[i]);
      // effect.tint = this.levelManager.CurrentTheme.isNight ? SplatEffectsLayer.hexMudNight : SplatEffectsLayer.hexMudDay;
      targets.push(effect);
    }

    this.showEffectStack({
      state: EffectStackState.Ready,
      targets: targets,
      endPoints: endPositions,
      effects: [
        { function: this.throwEffect.bind(this) },
        { function: this.slideEffect.bind(this), params: { duration: 1500 } },
        { function: this.fadeOutEffect.bind(this) }
      ]
    });
  }

  protected randomSplats(numberRequired: number, bank: string[]): string[] {
    let splats: string[] = [];

    if (numberRequired > bank.length) {
      let ratio = Math.floor(numberRequired / bank.length);

      for (let i = 0; i < ratio; i++) {
        splats.push(...bank);
      }
    }

    let remaining = numberRequired - splats.length;
    let splatBank = bank.slice();

    for (let i = 0; i < remaining; i++) {
      let rand = Phaser.Math.Between(0, splatBank.length - 1);
      let splat = splatBank.splice(rand, 1)[0];
      splats.push(splat);
    }

    return splats;
  }

  protected shutdown() {
    this.scene.events.off(SplatEffectsLayer.events.SplatBanana, this.showBananaSplats, this);
    this.scene.events.off(SplatEffectsLayer.events.SplatConcrete, this.showConcreteSplats, this);
    this.scene.events.off(SplatEffectsLayer.events.SplatOil, this.showOilSplats, this);
    this.scene.events.off(SplatEffectsLayer.events.SplatIce, this.showIceSplats, this);
    this.scene.events.off(SplatEffectsLayer.events.SplatLeaf, this.showLeafSplats, this);
    this.scene.events.off(SplatEffectsLayer.events.SplatMud, this.showMudSplats, this);
    this.scene.events.off(SplatEffectsLayer.events.SplatSnow, this.showSnowSplats, this);
    this.scene.events.off(SplatEffectsLayer.events.SplatSmoke, this.showSmokeSplats, this);

    super.shutdown();
  }
}
